import { Dialog } from "@in/component-library";
import React, { FC, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { ApplicationV2 } from "../../../main/models/application-v2";
import { UserAccount } from "../../../main/models/user-account";
import { UserTask } from "../../../main/models/user-task";
import { numberToWord } from "../../../utils/numberToWordConverter";
import { sendSiteimproveEvent } from "../../../utils/sendSiteimproveEvent";
import { getUserAccounts } from "../../apis/getUserAccounts";
import { useLayout } from "../../hooks/useLayout";
import { useLocale } from "../../hooks/useLocale";
import { Card } from "../card/card";
import NotificationList from "../notificationList/notificationList";
import "./userTasksSummary.scss";

const UserTasksSummary: FC = () => {
  const { data: userAccounts, isLoading } = useSWR(`userAccounts`, getUserAccounts);
  const { locale } = useLocale();
  const { translations } = useLayout();
  const [showModal, setShowModal] = useState(false);
  const [tasks, setTasks] = useState<UserTask[]>([]);
  const [messageTasks, setMessageTasks] = useState<UserTask[]>([]);
  const [notMessageTasks, setNotMessageTasks] = useState<UserTask[]>([]);

  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (showModal) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [showModal]);

  useEffect(() => {
    setTasks(getTasks());
  }, [userAccounts]);

  const onCloseModal = () => {
    setShowModal(false);
  };

  const getTasks = () => {
    const tasks: UserTask[] = [];
      userAccounts?.forEach((userAccount: UserAccount) => {
          tasks.push(...userAccount.applications.flatMap((application: ApplicationV2) => application.tasks.filter(task => task.isNew)));
    });

    tasks.sort((a, b) => {
      return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime();
    });

    const messages = tasks.filter((task) => task.type === "Message" && task.isNew);
    setMessageTasks(messages);
    const notMessages = tasks.filter((task) => task.type !== "Message" && task.isNew);
    setNotMessageTasks(notMessages);

    return tasks;
  };

  const translationToShow =
    messageTasks.length > 1 && notMessageTasks.length > 1
      ? translations.amountOfNotifications
          .replace("{var1}", numberToWord(messageTasks.length, locale))
          .replace("{var2}", numberToWord(notMessageTasks.length, locale))
      : messageTasks.length === 1 && notMessageTasks.length > 1
      ? translations.oneNewMessageAndMultipleTasks.replace("{var1}", numberToWord(notMessageTasks.length, locale))
      : messageTasks.length > 1 && notMessageTasks.length === 1
      ? translations.oneNewTaskAndMultipleMessages.replace("{var1}", numberToWord(messageTasks.length, locale))
      : notMessageTasks.length > 1
      ? translations.amountOfNewTasks.replace("{var1}", numberToWord(notMessageTasks.length, locale))
      : messageTasks.length > 1
      ? translations.amountOfNewMessages.replace("{var1}", numberToWord(messageTasks.length, locale))
      : messageTasks.length === 1 && notMessageTasks.length === 1
      ? translations.oneNewMessageAndTask
      : messageTasks.length === 1
      ? translations.oneNewMessage
      : notMessageTasks.length === 1
      ? translations.oneNewTask
      : translations.noNewNotifications;

  return !isLoading ? (
    <>
      <Card
        variant="primary"
        role="sidekick"
        headingLevel="subheading"
        title={translations.summary}
        lead={translationToShow}
        onClick={() => {
          setShowModal(true);
          sendSiteimproveEvent(`MyPage-openSummaryBeta`, "SummaryClicked", "Summary opened");
        }}
        onKeyDown={(e) => {
          if (e.code === "Enter") {
            setShowModal(true);
            sendSiteimproveEvent(`MyPage-openSummaryBeta`, "SummaryClicked", "Summary opened");
          }
        }}
        tabIndex={0}
      />
      <Dialog className="modal" title={translations.newNotifications} ref={dialogRef} onClose={onCloseModal}>
        <NotificationList tasks={tasks} onClose={onCloseModal} />
      </Dialog>
    </>
  ) : null;
};

export default UserTasksSummary;
